import { ActionTree } from 'vuex';
import { State } from '@/store/modules/k8/k8CommonState/k8.store';
import { K8_NAVTABS } from '@/utils/templates/k8mTemplate/k8m.constants';
import {
  K8MutationType,
  K8SetMutationType,
  K8SetCurrentMutationType,
} from '@/components/K8M/k8.types';
import { AxiosResponse } from 'axios';
import graphQl from '@/services/graphQl';
import { Context } from '@/utils/types/global';
import graphHelpers from '@/utils/graphHelper';
import { k8Scopes, DELIMITER } from '@/store/modules/k8/k8-types';

const actions: ActionTree<State, any> = {
  GET_K8_CLUSTERS(
    context: Context,
    params: { duration: any; serviceReq: any; pagination: any; keyword: String },
  ) {
    if (!params.serviceReq) {
      params.serviceReq = {
        capability: 'K',
      };
    }
    return graphQl
      .query('queryK8services')
      .params(params)
      .then((res: AxiosResponse) => {
        if (res.data.data.services.services.length > 0) {
          const curService = res.data.data.services.services.find(
            (ser) =>
              ser.key ===
              context.rootState.k8State.k8CommonState.currentCluster.key
          );
        } else {
          context.commit('setCurrentCluster', { key: '', label: '' });
        }
        return res;
      });
  },

  async GET_K8_ATTRIBUTES(
    context: Context,
    params: { request: any; duration: any }
  ) {
    return graphQl
      .query('getK8MetricsAttributes')
      .params(params)
      .then((res: AxiosResponse) => {
        return res.data.data.k8sGetMetaProperties ?? [];
      });
  },

  async GET_K8_NAMESPACES(
    context: Context,
    params: { duration: any; serviceId: any; properties: any }
  ) {
    return graphQl
      .query('getK8InstanceByMetaProperties')
      .params(params)
      .then((res: AxiosResponse) => {
        if (res.data.data.instanceId.length > 0) {
          const list = new Array();
          list.push({ key: 'all', label: 'All' });
          list.push(...res.data.data.instanceId);
          const modifiedList = list.map((instance) => {
            const namespaceSplit = instance.label.split(DELIMITER);
            return (
              {
                key: instance.key,
                label: instance.label.includes(DELIMITER) ? namespaceSplit[namespaceSplit.length -1] : instance.label,
              }
            );
          });
          context.commit('setNamespaces', modifiedList);
          const currentValue = modifiedList.find(
            (ser) =>
              ser.key ===
              context.rootState.k8State.k8CommonState.currentNamespace.key
          );
          context.commit(
            'setCurrentNamespace',
            currentValue || { key: 'all', label: 'All' }
          );
        } else {
          context.commit('setNamespaces', [{ key: 'all', label: 'All' }]);
          context.commit('setCurrentNamespace', { key: 'all', label: 'All' });
        }
      })
      .catch((err) => {
        console.log('Error', err);
      });
  },

  async GET_K8_WORKLOADS(
    context: Context,
    params: { duration: any; serviceId: any; properties: any }
  ) {
    return graphQl
      .query('getK8InstanceByMetaProperties')
      .params(params)
      .then((res: AxiosResponse) => {
        const currentWorkLoad =
          context.rootState.k8State.k8CommonState.currentWorkload?.key;
        if (res.data.data.instanceId.length > 0) {
          const list = new Array();
          list.push({ key: 'all', label: 'All' });
          list.push(...res.data.data.instanceId);
          const modifiedList = list.map((instance) => ({
            key: instance.key,
            label: instance.label.includes(':::')
              ? instance.label.split(':::')[2]
              : instance.label,
          }));
          context.commit(K8SetMutationType[currentWorkLoad], modifiedList);
          const currentData = modifiedList.find(
            (ser) =>
              ser.key ===
              context.rootState.k8State.k8CommonState[
                K8MutationType[currentWorkLoad]
              ].key
          );
          context.commit(
            K8SetCurrentMutationType[currentWorkLoad],
            currentData || { key: 'all', label: 'All' }
          );
        } else {
          context.commit(K8SetMutationType[currentWorkLoad], [
            {
              key: 'all',
              label: 'All',
            },
          ]);
          context.commit(K8SetCurrentMutationType[currentWorkLoad], {
            key: 'all',
            label: 'All',
          });
        }
      })
      .catch((err) => {
        console.log('Error', err);
      });
  },

  async GET_K8_CARDS_PROPERTIES(
    context: Context,
    params: { request: any; duration: any; valueColumn: any }
  ) {
    return graphQl
      .query('getK8MetricCardProperties')
      .params(params)
      .then((res: AxiosResponse) => {
        return res?.data?.data?.k8sLatestMetric?.metricsValue ?? [];
      });
  },
  /**
   *
   * @param context
   * @param params
   * @returns Promise with the required data
   */
  async getK8GraphData(context: Context, params: any) {
    const normal = true;
    let config = params.config;
    const  isCompareOptionsSelected  = context.rootState.globalState.settings.isCompareOptionsSelected;
    if (!config || !config.metricName || !config.queryMetricType) {
      return;
    }
    const { currentCluster, currentNode, currentPod, currentK8Tab } = context.rootState.k8State.k8CommonState;
    const metricNames = graphHelpers.parse(config.metricName);

    const labels = graphHelpers.parse(config.labelsIndex || '');

    const variablesList: Array<any> = metricNames.map((name: string) => {
      let variables = {} as any;

      variables = {
        condition: {
          name,
          entity: {
            scope: config.entityType ? config.entityType : k8Scopes.SERVICE,
            serviceName: currentCluster.key,
            serviceInstanceName:
              currentK8Tab === K8_NAVTABS.K8M_NODE_TAB
                ? currentNode.key
                : currentK8Tab === K8_NAVTABS.K8M_CLUSTER_TAB
                  ? currentCluster.key
                  : currentPod.key,
            normal,
          },
        },
        labels,
      };
      if (isCompareOptionsSelected && (config.queryMetricType === "readMetricsValues")) {
        variables.durationCurrent = params.duration;
        variables.durationCompare = params.compareDuration;
      } else {
        variables.duration = params.duration;
      }
      return variables;
    });
    if (!variablesList || !variablesList.length) {
      return;
    }
    let query = isCompareOptionsSelected ? config.queryMetricType + 'Compare' : config.queryMetricType;
    return Promise.all(
      variablesList.map((variable: any) => {
        if (variable) {
          return graphQl
            .graphQuery(variable, query)
            .then((res: AxiosResponse<any>) => {
              const resData = res.data.data;
              return {
                ...resData,
                config,
                metricName: variable.condition.name,
              };
            });
        } else {
          return { config };
        }
      }),
    )
      .then((data: any) => {
        return data;
      })
      .catch((err) => {
        console.log('Error', err);
      });
  },

  async getK8CardsData(
    context: Context,
    params: {
      request: {
        metricNames: any;
        entity: {
          scope: String;
          serviceName: String;
          normal: Boolean;
          serviceInstanceName: String;
          endpointName: String;
        };
      };
      duration: {
        start: String;
        end: String;
        step: String;
      };
      valueColumn: String;
    },
  ) {
    if (!params) {
      return new Promise<void>((resolve) => resolve());
    }
    let obj = params;

    const { currentGKECluster } = context.rootState.cloudState.services;
    if (!currentGKECluster || !currentGKECluster.key) {
      return;
    }
    obj.request.entity.serviceName = currentGKECluster.key;
    return graphQl
      .query('k8sDashboard')
      .params(obj)
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data.k8sDashboard.metricsValue;
        } else return [];
      })
      .catch((err) => {
        console.log('Error', err);
      });
  },
  async getK8TableData(
    context: Context,
    params: {
      tableOverviewRequest: any;
      duration: any;
      pagination: any;
    }
  ) {
    if (!params) {
      return new Promise<void>((resolve) => resolve());
    }

    return graphQl
      .query('k8sTable')
      .params(params)
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data?.k8sGetTableOverview;
        } else return [];
      })
      .catch((err) => {
        console.log('Error', err);
      });
  },
  async getK8NamespaceTableData(
    context: Context,
    params: {
      tableOverviewRequest: any;
      duration: any;
      pagination: any;
      instanceList: any
    }
  ) {
    if (!params) {
      return new Promise<void>(resolve => resolve());
    }

    return graphQl
      .query('k8sNamespaceTable')
      .params(params)
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data?.k8sNamespaceTableMetrics;
        } else return [];
      });
  },
  async GET_K8_SERVICE_INSTANCE_COUNT(
    context: Context,
    params: {
      serviceId: any;
      duration: any;
      instanceTypes: any;
    }
  ) {
    if (!params) {
      return new Promise<void>(resolve => resolve());
    }

    return graphQl
      .query('k8sServiceInstanceCount')
      .params(params)
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data?.k8sServiceInstanceCount;
        } else return [];
      });
  },
  async GET_K8_INSTANCES(
    context: Context,
    params: {
      K8sGetAllInstances: any;
      duration: any;
    }
  ) {
    if (!params) {
      return new Promise<void>((resolve) => resolve());
    }
    return graphQl
      .query('getK8AllInstances')
      .params(params)
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data?.instanceId;
        } else return [];
      });
  },
};

export default actions;
